import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import api, { clearSupportSession, setSupportAccessToken } from "@/modules/shared/api/axios";
import { fetchUser } from "@/modules/auth/store/authSlice";

export default function SupportHandoffPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState("");
  const [stage, setStage] = useState("Preparing read-only customer access...");
  // The link works once, so the exchange must not run a second time when React
  // mounts the page twice in development.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const exchange = async () => {
      try {
        const response = await api.get(`/support/consume/${encodeURIComponent(token || "")}`, {
          params: { json: 1 },
          forceRefresh: true,
          timeout: 20000,
        });
        const accessToken = response?.data?.data?.access_token;
        if (!accessToken) throw new Error("Support access could not be started.");

        setSupportAccessToken(accessToken);
        try {
          sessionStorage.setItem("tarh_support_handoff", "1");
        } catch (_) {
          // The token itself remains memory-only.
        }

        // The app started before this session existed, so it has no customer
        // loaded. Load the customer now; without this the dashboard sees nobody
        // signed in and sends the operator to the login page.
        setStage("Loading the customer account...");
        await dispatch(fetchUser({ forceRefresh: true, timeout: 20000 })).unwrap();
        navigate("/tenders?supported=1", { replace: true });
      } catch (failure) {
        clearSupportSession();
        const status = failure?.response?.status;
        setError(status === 401 || status === 403 || status === 404 || status === 410
          ? "This support link is invalid or has expired. Return to the Portal and reconnect."
          : "The support connection could not be completed. The server may be slow or unavailable. Return to the Portal and reconnect.");
      }
    };

    exchange();
  }, [dispatch, navigate, token]);

  return (
    <main className="min-h-screen grid place-items-center bg-slate-50 p-6 text-slate-800">
      <section className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
        <h1 className="text-lg font-semibold">Opening support session</h1>
        <p className="mt-2 text-sm text-slate-600" role={error ? "alert" : "status"}>{error || stage}</p>
      </section>
    </main>
  );
}
